import { type ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react'

import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'

import { useAppSelector } from '~/store'
import { getTranslation } from '~/tools/utils'
import { formatAmount } from '../sale'
import SaleSection from './sale-section'

const SALE_RETURN_STATUS = {
    requested: gettext('Requested'),
    rejected: gettext('Rejected'),
    accepted: gettext('Accepted'),
    canceled: gettext('Canceled'),
    channel_dispute: gettext('Channel dispute'),
    completed: gettext('Completed'),
    in_fulfillment: gettext('In fulfillment'),
} as const

type StockUnit = {
    id: number
    sku: string
    name: string
}

type SaleLineItem = {
    id: number
    sku: string
    stock_unit_name: string
    product_name: string
    price: string
    price_paid: string | null
    unit_price: string
    num_sold: number
    status: string
    has_return: boolean
    has_refund: boolean
    return_status: keyof typeof SALE_RETURN_STATUS
    gift_with_purchase_campaign_name: string | null
    item_level_discount?: number
    image?: string
    stock_unit: StockUnit & {
        product?: {
            translations: Record<string, unknown>
            cover_image_url?: string
        }
    }
}

type Sale = {
    currency: string
    status: string
    can_edit_items: boolean
    items_count: number | null
    shop: {
        extra_info: {
            can_set_shipping_provider: boolean
        }
    }
    sale_returns: Array<{
        status: keyof typeof SALE_RETURN_STATUS
        return_type: string
        sale_return_line_items: Array<{
            sale_line_item: number
        }>
        sale_line_item: number[]
    }>
    sale_line_items: SaleLineItem[]
}

const SaleItemInput = styled((props: TextFieldProps) => (
    <TextField
        size="small"
        margin="dense"
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiInputBase-input': {
        textAlign: 'center',
        width: 60,
        fontSize: '0.9rem',
        padding: theme.spacing(1),
    },
}))

type SaleLineItemProps = {
    sale: Sale
    item: SaleLineItem
    isEditable: boolean
    onUnitPriceChange?: (stockUnit: StockUnit, value: number) => void
    onStockUnitQuantityChange?: (stockUnit: StockUnit, delta: number) => void
    onDiscountChange?: (stockUnit: StockUnit, value: number) => void
}

function SaleLineItemColumn({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div>
            <Typography variant="caption" color="grey.500">
                {title}
            </Typography>
            <Typography variant="body2">{children}</Typography>
        </div>
    )
}

export function SaleLineItem({
    sale,
    item,
    isEditable,
    onUnitPriceChange,
    onStockUnitQuantityChange,
    onDiscountChange,
}: SaleLineItemProps) {
    const language = useAppSelector((state) => state.initial.language)

    const name = item?.stock_unit?.product
        ? getTranslation(item.stock_unit.product, language, 'name')
        : item.product_name
    const imageURL = item.image || item?.stock_unit?.product?.cover_image_url
    const pricePerItem = formatAmount(item.unit_price || item.price_paid, sale.currency)
    const sku = item.sku || item?.stock_unit?.sku
    const sku_name = item.stock_unit_name || item?.stock_unit?.name

    const [saleItem, setSaleItem] = useState(item)
    const [displayAmount, setDisplayAmount] = useState(item.num_sold)
    const [discount, setDiscount] = useState(
        item.item_level_discount && Number(item.item_level_discount)
    )
    const [unitPrice, setUnitPrice] = useState(item.unit_price && Number(item.unit_price))

    const handleDeleteClick = () => {
        onStockUnitQuantityChange?.(item.stock_unit, -item.num_sold)
    }

    const updateNumSold = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.valueAsNumber
        const delta = value - saleItem.num_sold

        setDisplayAmount(value)
        onStockUnitQuantityChange?.(item.stock_unit, delta)
    }

    const updateDiscount = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.valueAsNumber
        setDiscount(value)
        onDiscountChange?.(item.stock_unit, value)
    }

    const updateUnitPrice = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.valueAsNumber
        setUnitPrice(value)
        onUnitPriceChange?.(item.stock_unit, value)
    }

    useEffect(() => {
        setSaleItem(item)
        setDisplayAmount(item.num_sold)
        setDiscount(item.item_level_discount && Number(item.item_level_discount))
        setUnitPrice(item.unit_price && Number(item.unit_price))
    }, [item])

    const total = (Number(unitPrice) || 0) * Number(saleItem.num_sold) - (Number(discount) || 0)
    const isItemCanceled = sale.status !== 'canceled' && item.status === 'canceled'

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                opacity: isItemCanceled ? 0.6 : 1,
                py: 2,
                '&:first-of-type': {
                    pt: 1,
                },
                '&:last-of-type': {
                    pb: 0,
                },
                '&:not(:last-of-type)': {
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                },
            }}
        >
            <img src={imageURL} width={40} height={40} />
            <Stack sx={{ flexGrow: 1 }}>
                <Typography variant="body2">{name}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                            variant="outlined"
                            size="small"
                            label={sku}
                            sx={{ color: 'grey.500' }}
                        />
                        {sku_name && (
                            <Typography sx={{ color: 'grey.500', fontSize: '0.8rem' }}>
                                {sku_name}
                            </Typography>
                        )}
                        {item.gift_with_purchase_campaign_name && (
                            <Chip
                                variant="outlined"
                                size="small"
                                label={`GWP: ${item.gift_with_purchase_campaign_name}`}
                            />
                        )}
                        {isItemCanceled && (
                            <Chip
                                color="error"
                                variant="outlined"
                                size="small"
                                label={gettext('Canceled')}
                            />
                        )}
                        {item.has_return && (
                            <Tooltip placement="top" title={SALE_RETURN_STATUS[item.return_status]}>
                                <Chip
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    label={gettext('Return')}
                                />
                            </Tooltip>
                        )}
                        {item.has_refund && (
                            <Tooltip placement="top" title={SALE_RETURN_STATUS[item.return_status]}>
                                <Chip
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    label={gettext('Refund')}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    {saleItem.num_sold > 0 && (
                        <Typography variant="body2" color="grey.500">
                            x{saleItem.num_sold}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {isEditable ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SaleItemInput
                                        type="number"
                                        value={unitPrice || ''}
                                        label={gettext('Price')}
                                        onChange={updateUnitPrice}
                                    />
                                    <span>x</span>
                                    <SaleItemInput
                                        type="number"
                                        value={displayAmount || ''}
                                        label={gettext('Quantity')}
                                        onChange={updateNumSold}
                                    />
                                </Box>
                                <SaleItemInput
                                    type="number"
                                    value={discount || ''}
                                    label={gettext('Discount')}
                                    onChange={updateDiscount}
                                />
                                <div>
                                    <Typography variant="caption" color="grey.500">
                                        {gettext('Total')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatAmount(total, sale.currency)}
                                    </Typography>
                                </div>
                            </Box>

                            <div>
                                <IconButton
                                    size="small"
                                    title={gettext('Delete sale item')}
                                    onClick={handleDeleteClick}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </Box>
                    ) : (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <SaleLineItemColumn title={gettext('Unit price')}>
                                {pricePerItem}
                            </SaleLineItemColumn>
                            <SaleLineItemColumn title={gettext('Price')}>
                                {formatAmount(item.price, sale.currency)}
                            </SaleLineItemColumn>
                            {!!discount && (
                                <SaleLineItemColumn title={gettext('Discount')}>
                                    {formatAmount(discount, sale.currency)}
                                </SaleLineItemColumn>
                            )}
                            <SaleLineItemColumn title={gettext('Total')}>
                                {formatAmount(total, sale.currency)}
                            </SaleLineItemColumn>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Stack>
    )
}

type SaleItemsListProps = {
    sale: Sale
    onStockUnitQuantityChange?: (stockUnit: StockUnit, delta: number) => void
    onUnitPriceChange?: (stockUnit: StockUnit, value: number) => void
    onDiscountChange?: (stockUnit: StockUnit, value: number) => void
}

function SaleItemsList({
    sale,
    onStockUnitQuantityChange,
    onUnitPriceChange,
    onDiscountChange,
}: SaleItemsListProps) {
    const returnInfoBySaleLineItem = useMemo(() => {
        const returnInfoBySaleLineItem: Record<
            number,
            {
                has_return: boolean
                has_refund: boolean
                return_status: keyof typeof SALE_RETURN_STATUS
            }
        > = {}

        sale.sale_returns?.forEach((sr) => {
            sr.sale_return_line_items.forEach((srli) => {
                const slid = srli.sale_line_item
                if (!returnInfoBySaleLineItem[slid]) {
                    returnInfoBySaleLineItem[slid] = {
                        has_return: false,
                        has_refund: false,
                        return_status: sr.status,
                    }
                }

                if (sr.return_type === 'return_with_refund') {
                    returnInfoBySaleLineItem[slid].has_return = true
                    returnInfoBySaleLineItem[slid].has_refund = true
                } else if (sr.return_type === 'refund_only') {
                    returnInfoBySaleLineItem[slid].has_refund = true
                } else if (sr.return_type === 'replacement') {
                    returnInfoBySaleLineItem[slid].has_return = true
                }
            })
        })

        return returnInfoBySaleLineItem
    }, [sale.sale_returns])

    const saleLineItemsBySku = useMemo(() => {
        const saleLineItems =
            sale.sale_line_items?.map((sli) => ({
                ...sli,
                ...(returnInfoBySaleLineItem[sli.id] || {}),
            })) || []

        return saleLineItems.sort((item1, item2) =>
            String(item1.sku).localeCompare(String(item2.sku))
        )
    }, [sale.sale_line_items, returnInfoBySaleLineItem])

    if (!sale.items_count) {
        return (
            <Box sx={{ alignContent: 'center', p: 5 }}>
                <Typography align="center" color="textSecondary" variant="body2">
                    {gettext('No items selected.')}
                </Typography>
            </Box>
        )
    }

    return (
        <Stack color="grey.700">
            {saleLineItemsBySku.map((item) => (
                <SaleLineItem
                    key={item.id}
                    sale={sale}
                    item={item}
                    onUnitPriceChange={onUnitPriceChange}
                    onStockUnitQuantityChange={onStockUnitQuantityChange}
                    onDiscountChange={onDiscountChange}
                    isEditable={!!onStockUnitQuantityChange}
                />
            ))}
        </Stack>
    )
}

type Props = {
    sale: Sale
    mode: string
    searchQuery: string
    onSearchFieldChange: (value: string) => void
    onStockUnitQuantityChange: (stockUnit: StockUnit, delta: number) => void
    onDiscountChange: (stockUnit: StockUnit, value: number) => void
    onUnitPriceChange: (stockUnit: StockUnit, value: number) => void
}

export default function SaleItems({
    sale,
    mode,
    searchQuery,
    onSearchFieldChange,
    onStockUnitQuantityChange,
    onDiscountChange,
    onUnitPriceChange,
}: Props) {
    const hasShop = !!sale.shop

    // disable editing items, user have to cancel this sale and create other one
    const allowItemEditing =
        sale.can_edit_items &&
        (mode === 'add' || sale.status === 'unpaid' || sale.status === 'waiting_for_stock')

    return (
        <SaleSection label={gettext('Items')}>
            <Stack>
                {allowItemEditing && (
                    <Tooltip title={hasShop ? '' : gettext('Please select a shop first')}>
                        <TextField
                            disabled={!hasShop}
                            value={searchQuery}
                            placeholder={gettext('To add items, enter product name or SKU')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="disabled" />
                                    </InputAdornment>
                                ),
                                style: { height: 30, fontSize: '0.9rem' },
                            }}
                            onChange={(e) => {
                                onSearchFieldChange(e.target.value)
                            }}
                        />
                    </Tooltip>
                )}
                <SaleItemsList
                    sale={sale}
                    onUnitPriceChange={onUnitPriceChange}
                    onDiscountChange={onDiscountChange}
                    onStockUnitQuantityChange={
                        allowItemEditing ? onStockUnitQuantityChange : undefined
                    }
                />
            </Stack>
        </SaleSection>
    )
}
